import React, { useState, useEffect, useMemo } from "react";
import { ChartDataService } from "../../services/ChartDataService";
import { useUnifiedAuth } from "../../auth/useUnifiedAuth";
import { useSAPDataStore } from "../../store/SAPDataStore";
import { useChartCache } from "../../hooks/useChartCache";
import { useThemedCharts } from "../../hooks/useThemedCharts";
import BaseStockChart from "./BaseStockChart";
import { usePresetMarkers, PresetToggle } from "./PresetMarkers";

interface MTBFDataPoint {
  date: string;
  mtbf: number;
  shift: string;
  maxRuntime: number;
}

// Backend returns { mtbf: { shift: { timestamp: value } }, max_runtime_detail: { shift: { timestamp: value } } }
// or may have additional keys
interface MTBFResults {
  mtbf?: Record<string, Record<string, number>>;
  max_runtime_detail?: Record<string, Record<string, number>>;
  [key: string]: unknown;
}

interface MTBFChartProps {
  machineSerialNo?: string;
  lineId?: string;
  shifts?: string[];
  className?: string;
}

const MTBFChart: React.FC<MTBFChartProps> = ({
  machineSerialNo,
  shifts = [],
  className = "",
}) => {
  const { token } = useUnifiedAuth();
  const { dateRange, getMachineById, getTargetForMachine } = useSAPDataStore();
  const { getStatusColors } = useThemedCharts();
  const [rawChartData, setRawChartData] = useState<MTBFDataPoint[]>([]);
  const [data, setData] = useState<MTBFResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Preset markers functionality
  const [showPresetMarkers, setShowPresetMarkers] = useState(false);
  const { presetData } = usePresetMarkers(machineSerialNo, showPresetMarkers);

  // Use chart cache for better performance
  const chartCache = useChartCache<MTBFResults>({
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxEntries: 30, // Cache up to 30 different chart configurations
  });

  const fetchMTBFData = async () => {
    // Don't fetch if no shifts selected
    if (!shifts || shifts.length === 0) {
      setData(null);
      setLoading(false);
      return;
    }

    if (!machineSerialNo || !dateRange.start || !dateRange.end) return;
    if (!token) {
      setError("Authentication required");
      return;
    }

    // Check cache first
    const cachedData = chartCache.getCachedData(
      "pc.mtbf",
      machineSerialNo,
      dateRange,
      shifts,
    );

    if (cachedData) {
      setData(cachedData);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(
        machineSerialNo,
        dateRange,
        shifts,
      );
      const response = await ChartDataService.fetchMTBF(params, token);

      if (response.success && response.data) {
        const responseData = response.data as any;

        console.log("📊 MTBFChart: Raw API response:", {
          success: response.success,
          responseKeys: Object.keys(responseData),
          data: responseData,
        });

        // Backend returns { status, results, error } - extract results
        const results = responseData.results || responseData;

        console.log("📊 MTBFChart: Extracted results:", {
          resultsKeys: results ? Object.keys(results) : "null",
          sampleShifts: Object.keys(results).slice(0, 5),
        });

        setData(results);
        chartCache.setCachedData(
          "pc.mtbf",
          machineSerialNo,
          dateRange,
          shifts,
          results,
        );

        // Process response data
        // Format: { "A1": { "timestamp": { max_runtime: 123, mtbf: 456 } } }
        const processedData: MTBFDataPoint[] = [];

        if (results && typeof results === "object") {
          // Iterate through shifts (A1, B1, C1, etc.)
          Object.keys(results).forEach((shift) => {
            // Skip non-shift keys like 'status', 'error', etc.
            if (
              shift === "status" ||
              shift === "error" ||
              shift === "mtbf" ||
              shift === "max_runtime_detail"
            )
              return;

            const shiftData = results[shift];
            if (typeof shiftData !== "object" || shiftData === null) return;

            // Only include if in selected shifts or no shifts filter
            if (shifts.length === 0 || shifts.includes(shift)) {
              Object.keys(shiftData).forEach((timestamp) => {
                const dataPoint = shiftData[timestamp];

                // Handle nested format: { max_runtime: xxx, mtbf: yyy }
                if (typeof dataPoint === "object" && dataPoint !== null) {
                  const cleanTimestamp = timestamp
                    .replace(" +0000 UTC", "")
                    .replace(" UTC", "");
                  const rawMtbf = dataPoint.mtbf;
                  const rawMaxRuntime = dataPoint.max_runtime || 0;

                  // mtbf can be null, use max_runtime as fallback for display
                  const mtbf =
                    rawMtbf !== null && rawMtbf !== undefined
                      ? typeof rawMtbf === "number"
                        ? rawMtbf
                        : parseFloat(rawMtbf) || 0
                      : 0;
                  const maxRuntime =
                    typeof rawMaxRuntime === "number"
                      ? rawMaxRuntime
                      : parseFloat(rawMaxRuntime) || 0;

                  processedData.push({
                    date: cleanTimestamp,
                    mtbf,
                    maxRuntime,
                    shift,
                  });
                }
                // Handle flat format: { "timestamp": mtbfValue }
                else if (typeof dataPoint === "number") {
                  const cleanTimestamp = timestamp
                    .replace(" +0000 UTC", "")
                    .replace(" UTC", "");
                  processedData.push({
                    date: cleanTimestamp,
                    mtbf: dataPoint,
                    maxRuntime: 0,
                    shift,
                  });
                }
              });
            }
          });
        }

        // Sort by timestamp (string comparison works for ISO timestamps)
        processedData.sort((a, b) => a.date.localeCompare(b.date));
        setRawChartData(processedData);
      } else {
        setError("Invalid response format");
      }
    } catch (err) {
      console.error("Error fetching MTBF data:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch MTBF data",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMTBFData();
  }, [machineSerialNo, dateRange.start, dateRange.end, shifts]);

  // Transform data for BaseStockChart — two time-series (MTBF + Maximum
  // Runtime) plotted over time, mirroring the previous grouped-column data but
  // on the stock chart (navigator/zoom, target line). Not split per shift.
  const { stockSeries, shiftByTs } = useMemo(() => {
    if (rawChartData.length === 0) {
      return { stockSeries: [], shiftByTs: new Map<number, string>() };
    }

    // Sort by date then shift so columns render in chronological order.
    const sorted = [...rawChartData].sort((a, b) => {
      const d = a.date.localeCompare(b.date);
      if (d !== 0) return d;
      return a.shift.localeCompare(b.shift);
    });

    const mtbfData: Array<[number, number]> = [];
    const maxRuntimeData: Array<[number, number]> = [];
    const shiftMap = new Map<number, string>();

    sorted.forEach((item) => {
      const ts = new Date(item.date).getTime();
      if (isNaN(ts)) return;
      mtbfData.push([ts, item.mtbf || 0]);
      maxRuntimeData.push([ts, item.maxRuntime || 0]);
      shiftMap.set(ts, item.shift);
    });

    return {
      stockSeries: [
        // Maximum Runtime as filled columns (the "envelope"); MTBF as a line on
        // top so the two never overlap-hide each other on the datetime axis.
        {
          name: "Maximum Runtime",
          data: maxRuntimeData,
          color: "#9EC9E8",
          type: "column" as const,
        },
        {
          name: "MTBF",
          data: mtbfData,
          color: "#1E3A5F",
          type: "spline" as const,
        },
      ],
      shiftByTs: shiftMap,
    };
  }, [rawChartData]);

  // Calculate dynamic target for the current machine
  const dynamicTarget = useMemo(() => {
    if (!machineSerialNo) return null;

    const machine = getMachineById(machineSerialNo);
    if (!machine) return null;

    // Get minMTBF target
    return getTargetForMachine(machine, "minMTBF");
  }, [machineSerialNo, getMachineById, getTargetForMachine]);

  // Prepare plot lines with dynamic target
  const plotLines = useMemo(() => {
    const lines = [];

    if (dynamicTarget !== null) {
      lines.push({
        value: dynamicTarget,
        color: getStatusColors.plotLine,
        dashStyle: "Dash" as const,
        width: 2,
        label: "Min MTBF Target",
      });
    }

    return lines;
  }, [dynamicTarget, getStatusColors.plotLine]);

  // Show loading state when loading or no data yet
  // Show loading animation first
  if (loading) {
    return (
      <div className={`w-full ${className}`}>
        <BaseStockChart type="column" height={500} series={[]} loading={true} />
      </div>
    );
  }

  // Check if we have any data - data is directly shift-keyed (A1, B1, etc.) or has mtbf property
  const hasValidData =
    data &&
    (Object.keys(data).some(
      (key) =>
        key !== "status" && key !== "error" && typeof data[key] === "object",
    ) ||
      Object.keys(data.mtbf || {}).length > 0);

  // Also check if we have any processed data points with non-zero mtbf values
  const hasNonZeroMTBF = rawChartData.some((d) => d.mtbf > 0);
  const hasMTBFData =
    hasValidData && (hasNonZeroMTBF || rawChartData.length > 0);

  if (!hasMTBFData || rawChartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">
            No MTBF Data
          </h3>
          <p className="theme-text-secondary">
            {rawChartData.length > 0 && !hasNonZeroMTBF
              ? "MTBF values are not calculated for this period."
              : "No MTBF data found for the selected period."}
          </p>
        </div>
      </div>
    );
  }

  // Helper function to format milliseconds as HH:MM:SS
  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const hoursStr = hours.toString().padStart(2, "0");
    const minutesStr = minutes.toString().padStart(2, "0");
    const secondsStr = seconds.toString().padStart(2, "0");

    return `${hoursStr}:${minutesStr}:${secondsStr}`;
  };

  return (
    <div className={`w-full ${className}`}>
      <BaseStockChart
        commentProps={{ chartType: "mtbf", machineBK: machineSerialNo }}
        type="column"
        height={500}
        title="MTBF Over Time"
        subtitle={machineSerialNo || "No machine selected"}
        series={stockSeries}
        yAxis={{
          title: "Time (HH:MM:SS)",
          min: 0,
          plotLines: plotLines,
        }}
        customOptions={{
          plotOptions: {
            spline: {
              lineWidth: 3,
              marker: { enabled: true, radius: 3 },
              zIndex: 5,
            } as any,
          },
          yAxis: {
            labels: {
              formatter: function (this: any) {
                return formatTime(this.value);
              },
            },
          },
          tooltip: {
            shared: true,
            formatter: function (this: any) {
              const ts = this.x;
              const shift = shiftByTs.get(ts) || "";
              const date = ts ? new Date(ts).toLocaleDateString() : "";
              let tooltip = `<b>Date:</b> ${date}`;
              if (shift) tooltip += ` &nbsp; <b>Shift:</b> ${shift}`;
              tooltip += "<br/>";
              const points = this.points || [this.point];
              points.forEach((point: any) => {
                if (!point || !point.series) return;
                tooltip +=
                  `<span style="color:${point.series.color}">●</span> ` +
                  `${point.series.name}: <b>${formatTime(point.y || 0)}</b><br/>`;
              });
              return tooltip;
            },
          },
        }}
        legend={{ enabled: true, position: "bottom" }}
        panZoom={{
          enabled: true,
          type: "x",
          mouseWheel: true,
          pinchZoom: true,
        }}
        navigator={{ enabled: true, height: 80 }}
        rangeSelector={{ enabled: false }}
        loading={loading}
        error={error}
        onRetry={fetchMTBFData}
        noDataMessage={
          !machineSerialNo
            ? "No machine selected"
            : !dateRange.start || !dateRange.end
              ? "Please select a date range"
              : !shifts || shifts.length === 0
                ? "No shifts selected or available for the selected timeframe"
                : "No MTBF data found for the selected period"
        }
        extraControls={
          <PresetToggle
            enabled={showPresetMarkers}
            onToggle={setShowPresetMarkers}
            presetCount={presetData.length}
          />
        }
      />
    </div>
  );
};

export default MTBFChart;
